/**
 * echoRegTools.js — кнопка «Совместить эхо-кадры»: обвязка над core/echoRegister. [10.19.24]
 *
 * ГДЕ СТОИТ. [12.0.46] Вкладка «ЭхоКГ» правой панели, раздел «Кадры». Было: панель «Анализ», новый раздел «УЗИ (эхокардиография)» — сразу под
 * «МРТ количественно»: это тот же класс инструментов «посчитай по кадрам и
 * скажи с оговорками», и врач ищет их в одном месте.
 *
 * ЧТО ДЕЛАЕТ КНОПКА СЕЙЧАС — расчёт и отчёт, без правки картинки: сдвиг каждого
 * кадра активной серии относительно ПЕРВОГО, качество каждого совмещения и
 * сводка дрейфа датчика в миллиметрах. Применение сдвигов к отображению —
 * отдельная работа в рендере, и я не делаю вид, что она уже есть: об этом
 * написано в самом окне. Отчёт при этом полезен сам по себе: он отвечает на
 * вопрос «дрожал ли датчик и насколько», и его числа можно скопировать.
 *
 * Кадры БЕРУТСЯ ПОДРЯД, как лежат в серии, — и это ограничение названо в окне
 * первой строкой: между фазами цикла стенка сокращается, и низкая корреляция на
 * таких парах — не ошибка совмещения, а его честный ответ «это не сдвиг».
 */

import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { getSeriesImageIds, getAllSeries, getDICOMTags } from '../loaders/loaderManager.js';
import { cornerstone } from '../core/viewportState.js';
import { совместитьФазу, оговоркиСовмещения } from '../core/echoRegister.js';

let _ac = null;
const $ = (id) => document.getElementById(id);
const МАКС_КАДРОВ = 48;   // больше — отчёт нечитаем, а дрейф виден и по 48

async function пикселиКадра(imageId) {
  const image = await cornerstone.imageLoader.loadAndCacheImage(imageId);
  const п = image.getPixelData();
  const данные = new Float32Array(п.length);
  for (let i = 0; i < п.length; i++) данные[i] = п[i];
  return { данные, ширина: image.columns, высота: image.rows };
}

export async function совместитьЭхо() {
  try {
    const серии = (() => { try { const s = getAllSeries?.() || []; return Array.isArray(s) ? s : Object.values(s); } catch { return []; } })();
    if (!серии.length) { showToast('Сначала откройте исследование', 'warning', 3000); return null; }
    const уз = серии.filter((с) => String(с.modality || '').toUpperCase() === 'US');
    const цель = (уз.length ? уз : серии)[(уз.length ? уз : серии).length - 1];
    if (!уз.length) showToast('УЗ-серий не нашлось — считаю по активной серии; для эхо смысл тот же: дрейф между кадрами', 'warning', 5000);

    const uid = цель.seriesUID || цель.seriesInstanceUID;
    const imageIds = (getSeriesImageIds?.(uid) || []).slice(0, МАКС_КАДРОВ);
    if (imageIds.length < 2) { showToast('В серии меньше двух кадров — совмещать нечего', 'warning', 4000); return null; }

    showToast(`Совмещаю ${imageIds.length} кадров с первым…`, 'info', 3000);

    const кадры = [];
    for (const id of imageIds) {
      try { кадры.push(await пикселиКадра(id)); } catch (e) { log.debug('[Эхо] кадр', e.message); }
    }
    if (кадры.length < 2) { showToast('Пиксели кадров не загрузились', 'warning', 4000); return null; }

    // Шаг пикселя в мм — для дрейфа в миллиметрах. У УЗ он в x00280030 или в
    // области калибровки; без него дрейф честно остаётся в пикселях.
    let ммНаПиксель = null;
    try {
      const теги = await getDICOMTags(imageIds[0]);
      const шаг = String(теги?.['x00280030']?.value || '').split('\\').map(Number);
      if (шаг.length === 2 && шаг.every((v) => v > 0)) ммНаПиксель = шаг;
    } catch (e) { /* нет шага — покажем пиксели */ }

    const и = совместитьФазу(кадры, {});
    показатьОкно({ серия: цель, кадров: кадры.length, и, ммНаПиксель });
    return и;
  } catch (e) {
    log.warn('[Эхо-совмещение]', e.message);
    showToast('Совмещение не посчиталось: ' + e.message, 'error', 6000);
    return null;
  }
}

function показатьОкно({ серия, кадров, и, ммНаПиксель }) {
  const фон = document.createElement('div');
  фон.style.cssText = 'position:fixed;inset:0;z-index:60000;background:rgba(5,8,14,0.72);display:flex;align-items:center;justify-content:center;padding:14px;overflow:auto;';
  const к = document.createElement('div');
  к.style.cssText = 'max-width:680px;width:100%;max-height:92vh;overflow:auto;background:#0f1420;border:1px solid #2a3546;border-radius:14px;padding:16px;color:#dbe6f5;font-size:13px;line-height:1.5;';
  фон.appendChild(к);
  фон.addEventListener('click', (e) => { if (e.target === фон) фон.remove(); });

  const вММ = (пикс) => (ммНаПиксель ? (пикс * (ммНаПиксель[0] + ммНаПиксель[1]) / 2).toFixed(1) + ' мм' : пикс.toFixed(1) + ' пикс');
  const строки = [];
  строки.push(`<div style="font-size:16px;font-weight:700;margin-bottom:8px;">Совмещение эхо-кадров — ${серия?.seriesDescription || 'серия'}</div>`);
  строки.push(`<div style="color:#9fb3cf;">Кадров ${кадров}, опора — первый. Кадры взяты ПОДРЯД: между фазами цикла стенка сокращается, и низкая корреляция на таких парах — честный ответ «это не сдвиг», а не ошибка.</div>`);

  if (!и.ок && !и.сдвиги?.length) {
    строки.push(`<div style="margin-top:10px;padding:10px;border-radius:8px;background:rgba(120,20,20,0.5);border:1px solid #e66;color:#ffd9d9;">${и.причина}</div>`);
  } else {
    const дрейфы = и.сдвиги.filter((с) => с.ок !== false && !с.опорный && Number.isFinite(с.dx)).map((с) => Math.hypot(с.dx, с.dy));
    const макс = дрейфы.length ? Math.max(...дрейфы) : 0;
    const сред = дрейфы.length ? дрейфы.reduce((a, b) => a + b, 0) / дрейфы.length : 0;
    строки.push(`<div style="margin-top:8px;font-weight:700;">Дрейф: средний ${вММ(сред)}, наибольший ${вММ(макс)} · совмещено надёжно ${и.совмещено}, ненадёжных ${и.ненадёжных}`
      + `${Number.isFinite(и.средняяНКК) ? ` · средняя корреляция ${и.средняяНКК.toFixed(2)}` : ''}</div>`);
    const ряды = и.сдвиги.slice(0, 40).map((с) => {
      if (с.опорный) return `<tr><td>${с.кадр + 1}</td><td colspan="4" style="color:#8fa3c0;">опора</td></tr>`;
      if (с.ок === false) return `<tr><td>${с.кадр + 1}</td><td colspan="4" style="color:#ff9c9c;">${с.причина}</td></tr>`;
      const метка = с.надёжно ? '<span style="color:#7ee787;">да</span>' : `<span style="color:#ffcf7d;">нет: ${с.причина}</span>`;
      return `<tr><td>${с.кадр + 1}</td><td>${с.dx.toFixed(1)}</td><td>${с.dy.toFixed(1)}</td><td>${с.нкк.toFixed(2)}</td><td>${метка}</td></tr>`;
    }).join('');
    строки.push(`<table style="width:100%;margin-top:8px;border-collapse:collapse;font-size:12px;">
      <tr style="color:#9fb3cf;text-align:left;"><th>кадр</th><th>dx, пикс</th><th>dy, пикс</th><th>НКК</th><th>надёжно</th></tr>${ряды}</table>`);
  }
  const огов = оговоркиСовмещения(и);
  огов.push('Кнопка считает и показывает сдвиги; отображение кадров она пока НЕ правит — применение сдвигов к показу серии будет отдельным шагом.');
  строки.push(`<div style="margin-top:10px;padding:9px 11px;border-radius:8px;background:rgba(60,50,10,0.5);border:1px solid #a88;color:#e8d9a8;">${огов.join('<br>')}</div>`);
  строки.push('<div style="margin-top:8px;color:#8fa3c0;">Не для диагностики.</div>');

  к.innerHTML = строки.join('');
  const кнопки = document.createElement('div');
  кнопки.style.cssText = 'display:flex;gap:8px;margin-top:12px;';
  const копир = document.createElement('button');
  копир.textContent = 'Скопировать';
  копир.style.cssText = 'flex:1;padding:10px;border-radius:8px;border:1px solid #2a3546;background:#1a2332;color:#dbe6f5;';
  копир.addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(к.innerText); showToast('Скопировано', 'success', 1500); }
    catch (e) { showToast('Выделите текст вручную', 'warning', 2500); }
  });
  const закр = document.createElement('button');
  закр.textContent = 'Закрыть';
  закр.style.cssText = копир.style.cssText;
  закр.addEventListener('click', () => фон.remove());
  кнопки.append(копир, закр);
  к.appendChild(кнопки);
  document.body.appendChild(фон);
}

export function initEchoReg() {
  if (_ac) _ac.abort();
  _ac = new AbortController();
  $('btn-echo-register')?.addEventListener('click', () => { совместитьЭхо(); }, { signal: _ac.signal });
  log.info('[Эхо-совмещение] кнопка привязана');
}

export default { initEchoReg, совместитьЭхо };
